import styled from 'styled-components';

import { CUST, CUST_ANIM, CUST_GRADIENTS, CUST_REDUCED_MOTION, CUST_SHADOWS } from '../../../../theme/customizer-tokens';

/** CDN device-frame images (provided by the design). */
export const FRAME_LAPTOP = 'https://micros-t.b-cdn.net/wl-laptop-frame-black-1773240059426.png';
export const FRAME_PHONE = 'https://micros-t.b-cdn.net/wl-phone-orange-frame-1771331265334.png';

/**
 * Dual-device preview (MacBook + iPhone) showing the live casino in each screen.
 *
 * Ported from the design's authoritative CSS: each `.device-container` is laid out
 * at its device's native pixel box and visually fit with `transform: scale()`,
 * where the scale factor derives from `--container-width` / `--container-height`
 * (set inline per resize by `useDimensions`). The phone wrapper is CSS-anchored to
 * the desktop wrapper (bottom-left). Notch / chin pick up the casino theme tokens
 * applied to `.device-screen`.
 */
export const Root = styled.div`
  width: 100%;
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: safe center;

  .preview-wrapper {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    user-select: none;

    .device-container {
      position: relative;

      .device-frame {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 10;
        pointer-events: none;
        background-size: 100% 100%;
        background-repeat: no-repeat;
      }

      .device-screen {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        border: none;
        z-index: 1;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        /* Solid brand body fill so the screen never shows through (the iframe stays
           transparent until it loads). It's the element's own background, painted
           below the phase scrim (::before/::after), so the gradient mask stays on top. */
        background: var(--bg-default, var(--body-bg-default, ${CUST.deviceFallbackBg}));

        .device-display {
          flex: 1;
          height: 100%;
          background-color: ${CUST.deviceFallbackBg};
        }

        iframe {
          width: 100%;
          height: 100%;
          overflow: scroll;
        }

        /* Phase overlays (toggled by .pending / .deploying on the root). The
           ::before dims/blurs the screen, the ::after adds the vignette/glow grid. */
        &::before,
        &::after {
          content: '';
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          display: block;
          pointer-events: none;
          transition:
            opacity ${CUST_ANIM.OVERLAY_MS}ms ease-in-out,
            box-shadow ${CUST_ANIM.OVERLAY_MS}ms ease-in-out,
            background ${CUST_ANIM.OVERLAY_MS}ms ease-in-out,
            backdrop-filter ${CUST_ANIM.OVERLAY_MS}ms ease-in-out;

          ${CUST_REDUCED_MOTION} {
            transition: none;
          }
        }

        &::before {
          z-index: 2;
          backdrop-filter: blur(0px);
        }

        /* The coloured edge-glow sits ABOVE the animated dot field (z-index 3),
           so the magenta→cyan vignette reads over the dots, as it did when the
           grid and glow shared this one pseudo-element. */
        &::after {
          z-index: 4;
        }
      }

      &[class*='smartphone'] {
        --fixed-width: 490;
        --fixed-width-px: 490px;
        --fixed-height: 996;
        --fixed-height-px: 996px;
        --scale-factor: min(
          1,
          calc(var(--container-width) / var(--fixed-width)),
          calc(var(--container-height) / var(--fixed-height))
        );
        --aspect-ratio: var(--fixed-width) / var(--fixed-height);

        width: var(--fixed-width-px);
        min-width: var(--fixed-width-px);
        max-width: var(--fixed-width-px);
        height: var(--fixed-height-px);
        min-height: var(--fixed-height-px);
        max-height: var(--fixed-height-px);

        aspect-ratio: var(--aspect-ratio);
        transform: scale(var(--scale-factor));

        .device-frame {
          background-image: url('${FRAME_PHONE}');
        }

        .device-notch {
          height: 66px;
          min-height: 66px;
          border-radius: 56px 56px 0 0;
          background: var(--top-nav-surface, ${CUST.deviceFallbackBg});
        }

        .device-chin {
          height: 34px;
          min-height: 34px;
          border-radius: 0 0 56px 56px;
          background: var(--bottom-nav-surface, ${CUST.deviceFallbackBg});
        }

        .device-screen {
          width: 440px;
          height: 956px;
          /* Clip the screen to the bezel's rounded cutout in EVERY phase (incl.
             live). Without it the sharp-cornered brand-fill rectangle pokes out
             past the phone frame's rounded corners on the "live" screen. The notch /
             chin already round to 56px, so this just matches them. */
          border-radius: 56px;
        }
      }

      &[class*='desktop'] {
        --fixed-width: 1972;
        --fixed-width-px: 1972px;
        --fixed-height: 1284;
        --fixed-height-px: 1284px;
        --scale-factor: min(
          1,
          calc(var(--container-width) / var(--fixed-width)),
          calc(var(--container-height) / var(--fixed-height))
        );
        --aspect-ratio: var(--fixed-width) / var(--fixed-height);

        width: var(--fixed-width-px);
        min-width: var(--fixed-width-px);
        max-width: var(--fixed-width-px);
        height: var(--fixed-height-px);
        min-height: var(--fixed-height-px);
        max-height: var(--fixed-height-px);

        aspect-ratio: var(--aspect-ratio);
        transform: scale(var(--scale-factor));

        .device-frame {
          background-image: url('${FRAME_LAPTOP}');
        }

        .device-notch {
          height: 34px;
          min-height: 34px;
          border-radius: 0;
          background: var(--top-nav-surface, ${CUST.deviceFallbackBg});
        }

        .device-chin {
          height: 0;
          min-height: 0;
          border-radius: 0;
          background: var(--bottom-nav-surface, ${CUST.deviceFallbackBg});
        }

        .device-screen {
          width: 1512px;
          height: 982px;
        }
      }
    }

    &:has([class*='desktop']) {
      width: auto;
      height: auto;
      aspect-ratio: 1.5;
      anchor-name: --preview-desktop;
    }

    &:has([class*='smartphone']) {
      width: auto;
      height: calc(anchor-size(--preview-desktop height) * 0.67);
      aspect-ratio: 2 / 3;
      position: absolute;
      bottom: calc(anchor(--preview-desktop bottom) + 8px);
      left: calc(anchor(--preview-desktop left) + 0px);
    }

    iframe {
      pointer-events: none;
      user-select: none;
    }
  }

  /* ── Variant: phone-only ─────────────────────────────────────────────────────
     The iPhone alone, standalone (no MacBook to anchor to): the wrapper fills the
     container and the phone scales to fit, centered. Overrides the dual-mode
     anchor positioning above. */
  &.phone-only .preview-wrapper:has([class*='smartphone']) {
    position: relative;
    inset: auto;
    bottom: auto;
    left: auto;
    width: 100%;
    height: 100%;
    aspect-ratio: auto;
  }

  /* ── Phase: pending (review) — screens dimmed/locked ─────────────────────── */
  &.pending {
    /* Dark "locked" dimming muted for now: the scrim (::before) + vignette (::after)
       are kept defined below but hidden via opacity. Bump these back up (or split
       per-pseudo) to tweak how much the review preview is darkened. */
    [class*='desktop'] .device-screen,
    [class*='smartphone'] .device-screen {
      &::before,
      &::after {
        opacity: 0;
      }
    }

    [class*='desktop'] .device-screen {
      border-radius: 0;

      &::before {
        background: ${CUST_GRADIENTS.lockedScrimDesktop};
      }

      &::after {
        box-shadow: ${CUST_SHADOWS.pendingVignetteDesktop};
      }
    }

    [class*='smartphone'] .device-screen {
      &::before {
        background: ${CUST_GRADIENTS.lockedScrimPhone};
      }

      &::after {
        box-shadow: ${CUST_SHADOWS.pendingVignettePhone};
      }
    }
  }

  /* ── Phase: deploying — screens blurred, animated dot field + edge glow ────
     The blur lives on ::before; the dot field is a real <canvas> overlay (see
     GridOverlay below + DevicePreview.tsx) so it can cross-fade in/out smoothly
     and animate per-frame; ::after now carries only the coloured edge glow. */
  &.deploying {
    [class*='desktop'] .device-screen,
    [class*='smartphone'] .device-screen {
      &::before {
        background: ${CUST.deployingBlurBg};
        backdrop-filter: blur(30px);
        opacity: 1;
      }
    }

    [class*='desktop'] .device-screen {
      border-radius: 0;

      &::after {
        box-shadow: ${CUST_SHADOWS.deployingGlowDesktop};
      }
    }

    [class*='smartphone'] .device-screen {
      &::after {
        box-shadow: ${CUST_SHADOWS.deployingGlowPhone};
      }
    }
  }
`;

/**
 * The animated dot field that fronts each blurred screen while deploying — a
 * real DOM overlay holding the {@link DotField} canvas (a pseudo-element can't
 * host a canvas). It cross-fades in when deploying begins and out as the site
 * deblurs, so the reveal mirrors the logo generation's grid dissolve.
 *
 * `plus-lighter` adds the near-white dots over the dark blur wash (a glowing
 * lattice); a soft radial mask feathers it out toward the screen corners. Sits
 * at z-index 3 — above the blur (::before, z2), below the edge glow (::after, z4).
 */
export const GridOverlay = styled.div<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
  mix-blend-mode: plus-lighter;
  -webkit-mask-image: ${CUST_GRADIENTS.deployGridMask};
  mask-image: ${CUST_GRADIENTS.deployGridMask};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity ${CUST_ANIM.OVERLAY_MS}ms ease-in-out;

  ${CUST_REDUCED_MOTION} {
    transition: none;
  }
`;

/**
 * Whitelabels.com loader shown over the screen while the preview iframe loads. Sits
 * ABOVE the phase scrim/glow (z-index 5) so it stays legible even under the locked
 * gradient mask, while the solid screen fill behind keeps the frame from showing
 * through. White art is darkened ($onLight) on a light brand fill so it reads on either.
 */
export const Loader = styled.img<{ $shown: boolean; $onLight: boolean }>`
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 5;
  width: 64px;
  height: 64px;
  transform: translate(-50%, -50%);
  object-fit: contain;
  pointer-events: none;
  filter: ${({ $onLight }) => ($onLight ? 'brightness(0)' : 'none')};
  opacity: ${({ $shown }) => ($shown ? 1 : 0)};
  transition: opacity ${CUST_ANIM.OVERLAY_MS}ms ease;
`;